/*
 * Strictly to be used by the react tsx pages. The SPI.Commons configuration are not available on the react page.
 * Thus, the clientConfigs are passed in as a parameter.
 */
import {
    AuthorizationStatusType,
    PimsActivateMyChartResponse,
    PimsAuthorizationResponse,
    PimsErrorResponse,
    PimsGetAuthorizationResponse,
    PimsGetVerificationSessionResponse,
    PimsPostVerificationSessionRequest,
    PimsPostVerificationSessionResponse,
    SessionStatus,
} from '../../../router/types';
import { ActivationCodeAuthorizationEvidence, DemographicsAuthorizationEvidence, InstantActivationEvidence } from '../../../types/authorizationEvidence';
import { PimsConsumer } from '../../../types/consumer';
import { Demographics } from '../../../types/demographics';
import { MFAVerifyType } from '../../../types/mfa';
import { OPENID_PARAMS } from '../../../types/openid_enum';
import { convertDateFormat } from '../../../utils/dateUtils';
import { CORRELATION_ID_HEADER, getClientCorrelationId } from '../../logger/logMetadata';
import { getAmplitudeIdentifiers } from '../amplitudeClient';
import { AuthRequest, DemographicsRequest, IdentityVerificationRequest } from './types';

export type SendVerificationCodeRequest = {
    jwt: string;
    pimsApiUrl: string;
    value: string;
    language: string;
    mode?: string;
};

export type VerifyCodeRequest = {
    jwt: string;
    pimsApiUrl: string;
    code: string;
    mode?: string;
    email?: string;
    proveBypass?: boolean;
};

export type ChangePasswordRequest = {
    jwt: string;
    pimsApiUrl: string;
    existingpassword: string;
    newpassword: string;
    language: string;
    brand: string;
};

export type LinkDemographicsRequest = {
    jwt: string;
    demographics: Demographics;
    pimsApiUrl: string;
    system: string;
};

type AuthorizationEvidence = ActivationCodeAuthorizationEvidence | DemographicsAuthorizationEvidence | InstantActivationEvidence;

export const PROVE_BYPASS_HEADER = 'prove-verification-bypass';

/**
 * The header name for the API deprecation force flight.
 */
export const API_DEPRECATION_HEADER_FORCE_FLIGHT = 'api-dep-condition';

/** Used to pass client information to PIMS for analytics and logging purposes */
export const CLIENT_ID_HEADER = 'client-id';

export function buildStandardHeaders(jwt: string): Record<string, string> {
    return {
        Authorization: 'Bearer ' + jwt,
        'Content-Type': 'application/json',
        [CORRELATION_ID_HEADER]: getClientCorrelationId(),
    };
}

export async function linkWithDemographics({ jwt, demographics, pimsApiUrl, system }: LinkDemographicsRequest) {
    if (demographics.dateOfBirth) {
        demographics.dateOfBirth = convertDateFormat(demographics.dateOfBirth);
    }
    const headers: Record<string, string> = buildStandardHeaders(jwt);
    const amplitudeIdentifiers = getAmplitudeIdentifiers();
    headers['amp-device-id'] = amplitudeIdentifiers.amplitudeDeviceId;
    headers['amp-session-id'] = String(amplitudeIdentifiers.amplitudeSessionId);

    const body = {
        demographics,
    };
    if (system) {
        body['ehrSystem'] = system;
    }
    return await fetch(`${pimsApiUrl}/v2/consumer/ehr-link/demographics`, {
        headers,
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function activateMyChart(jwt: string, pimsApiUrl: string, ehrSystem: string): Promise<PimsActivateMyChartResponse> {
    const headers = buildStandardHeaders(jwt);
    try {
        const response = await fetch(`${pimsApiUrl}/v2/consumer/mychart/activate`, {
            headers,
            body: JSON.stringify({
                ehrSystem,
            }),
            method: 'PUT',
        });
        const body = (await response.json()) as PimsActivateMyChartResponse;
        return body;
    } catch (error: any) {
        return {
            wasSuccessful: false,
            error: error.message,
        };
    }
}

export async function sendVerificationCode({ jwt, value, pimsApiUrl, language, mode = MFAVerifyType.PhoneVerifyType }: SendVerificationCodeRequest) {
    language = language === 'espanol' || language === 'es' ? 'es' : 'en';

    const searchParams = new URLSearchParams(document.location.search);
    const brand = searchParams.get(OPENID_PARAMS.BRAND) || '';
    const body = {
        mode: mode,
        value: value,
        brand: brand,
        language: language,
    };

    const headers: Record<string, string> = buildStandardHeaders(jwt);
    return await fetch(`${pimsApiUrl}/v1/util/code`, {
        headers,
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function verifyCode({ jwt, code, pimsApiUrl, mode = MFAVerifyType.PhoneVerifyType, email = '', proveBypass = false }: VerifyCodeRequest) {
    const body = {
        mode: mode,
        code: code,
        ...(email && { email }),
    };
    const headers: Record<string, string> = buildStandardHeaders(jwt);
    if (proveBypass) {
        headers[PROVE_BYPASS_HEADER] = 'bypass';
    }

    return await fetch(`${pimsApiUrl}/v1/util/code/verify`, {
        headers,
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function changePassword(request: ChangePasswordRequest) {
    const { brand, existingpassword, newpassword, pimsApiUrl, jwt } = request;
    const language = request.language === 'espanol' || request.language === 'es' ? 'es' : 'en';

    const body: { [key: string]: string | boolean } = {
        newPassword: newpassword,
        oldPassword: existingpassword,
        language,
        sendEmail: true,
    };
    if (brand) {
        body.brand = brand;
    }

    const headers = buildStandardHeaders(jwt);
    return await fetch(`${pimsApiUrl}/v2/consumer/password`, {
        headers,
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/**
 * Fetches the patient consumer. If enforceEhrSystem is given, it applies a filter to the request which
 * filters the authorized EHR system of the consumer.
 * @param jwt
 * @param pimsApiUrl
 * @param enforceEhrSystems
 */
export async function getConsumer(jwt: string, pimsApiUrl: string, enforceEhrSystems?: string): Promise<PimsConsumer> {
    const headers = buildStandardHeaders(jwt);
    // Retrieve the ID and authorization status and filter by EHR System, if necessary
    let url = `${pimsApiUrl}/v2/consumer?scope=ids%20authorization`;
    if (enforceEhrSystems) {
        url += `&filter=externalIdentifiers.system=${enforceEhrSystems}`;
    }
    const response = await fetch(url, {
        headers,
        method: 'GET',
        signal: AbortSignal.timeout(10000),
    });

    const consumer = await response.json();
    return consumer;
}

export async function getAutoInstantActivationAuthorization(
    jwt: string,
    pimsApiUrl: string,
    ehrSystem: string,
    instantActivationCode: string,
): Promise<PimsGetAuthorizationResponse> {
    const getAuthorizationParams = new URLSearchParams({
        ehrSystem,
        instantActivationCode,
    });
    const headers = buildStandardHeaders(jwt);
    try {
        const response = await fetch(`${pimsApiUrl}/v2/workflow/embedded-code/authorized?` + getAuthorizationParams.toString(), {
            headers,
            method: 'GET',
        });
        const body = (await response.json()) as PimsGetAuthorizationResponse;
        return body;
    } catch {
        return {
            aiaCodeAuthorizationStatus: AuthorizationStatusType.UnknownError,
        };
    }
}

export async function postIdentityVerificationSession({
    jwt,
    pimsApiUrl,
    brand,
    ehrSystem,
    deviceType,
    redirectUri,
    customScheme,
    vendor,
    language = 'en',
}: PimsPostVerificationSessionRequest): Promise<PimsPostVerificationSessionResponse> {
    const body = { brand, ehrSystem, deviceType, redirectUri, customScheme, language };
    const headers = buildStandardHeaders(jwt);
    const response = await fetch(`${pimsApiUrl}/v2/workflow/verification-session/${vendor}`, {
        headers,
        body: JSON.stringify({ ...body, deviceType: deviceType.toLowerCase() }),
        method: 'POST',
    });

    const { id, token } = await response.json();
    return { verificationSessionId: id, sessionTokenId: token };
}

/**
 *
 * @param jwt
 * @param pimsApiUrl
 * @param vendor
 * @param sessionId
 * @returns
 */
export async function getIdentityVerificationSession(jwt: string, pimsApiUrl: string, vendor: string, sessionId: string): Promise<PimsGetVerificationSessionResponse> {
    const headers = buildStandardHeaders(jwt);
    try {
        const response = await fetch(`${pimsApiUrl}/v2/workflow/verification-session/${vendor}/${sessionId}`, {
            headers,
            method: 'GET',
        });
        const verificationSession = (await response.json()) as PimsGetVerificationSessionResponse;
        return verificationSession;
    } catch {
        return {
            status: SessionStatus.FAILED,
        };
    }
}

/**
 *
 * @param url
 * @param headers
 * @param params
 */
export const authorizeBy = <T extends AuthorizationEvidence>(url: URL, method: string, headers: Record<string, string>, params: AuthRequest<T>): Promise<Response> => {
    const { ehrSystem, clientId, ...body } = params;

    if (ehrSystem) {
        body['ehrSystem'] = ehrSystem;
    }

    if (clientId) {
        headers[CLIENT_ID_HEADER] = clientId;
    }

    /**
     * If the apiDepCondition is present, add the header and delete the parameter from the request body
     * This is used to testing and validation of API Deprecation work
     */
    if (body.apiDepCondition && body.apiDepCondition !== '') {
        headers[API_DEPRECATION_HEADER_FORCE_FLIGHT] = body.apiDepCondition;
        delete body.apiDepCondition;
    }

    const amplitudeIdentifiers = getAmplitudeIdentifiers();
    headers['amp-device-id'] = amplitudeIdentifiers.amplitudeDeviceId;
    headers['amp-session-id'] = String(amplitudeIdentifiers.amplitudeSessionId);

    return fetch(url.toString(), {
        headers,
        method,
        body: JSON.stringify({ ...body }),
    });
};

export async function authorizeByActivationCode(
    url: URL,
    method: string,
    headers: Record<string, string>,
    params: AuthRequest<ActivationCodeAuthorizationEvidence>,
): Promise<Response> {
    const { authorizationEvidence } = params;
    authorizationEvidence.dateOfBirth = convertDateFormat(authorizationEvidence.dateOfBirth);
    return authorizeBy<ActivationCodeAuthorizationEvidence>(url, method, headers, params);
}

export async function authorizeByDemographics(url: URL, method: string, headers: Record<string, string>, params: DemographicsRequest): Promise<Response> {
    // the proveByPass comes from the evidence, but it is not a part of the request body
    // it is meant to be in the request header
    if (params.proveBypass) {
        headers[PROVE_BYPASS_HEADER] = params.proveBypass;
        delete params.proveBypass;
    }
    const { authorizationEvidence } = params;
    authorizationEvidence.demographics.dateOfBirth = convertDateFormat(authorizationEvidence.demographics.dateOfBirth);
    return authorizeBy<DemographicsAuthorizationEvidence>(url, method, headers, params);
}

export async function authorizeByInstantActivation(
    url: URL,
    method: string,
    headers: Record<string, string>,
    params: AuthRequest<InstantActivationEvidence>,
): Promise<Response> {
    const { authorizationEvidence } = params;
    if ('instantActivationCode' in authorizationEvidence) {
        authorizationEvidence.instantActivationCode = authorizationEvidence.instantActivationCode.replace(' ', '+');
    }
    authorizationEvidence.dateOfBirth = convertDateFormat(authorizationEvidence.dateOfBirth);
    return authorizeBy<InstantActivationEvidence>(url, method, headers, params);
}

/**
 * Sends a PIMS Request to authorize the user using identity verification.
 * @param url The URL for this API
 * @param headers Mandatory headers for the request
 * @param params Additional parameters needed by PIMS: vendor and sessionId.
 */
export async function authorizeByIdentityVerification(
    url: URL,
    method: string,
    headers: Record<string, string>,
    params: IdentityVerificationRequest,
): Promise<PimsAuthorizationResponse> {
    const { vendor, clientId, ...body } = params;
    url.pathname = url.pathname.replace(/\/$/, '') + '/' + vendor;

    const amplitudeIdentifiers = getAmplitudeIdentifiers();
    headers['amp-device-id'] = amplitudeIdentifiers.amplitudeDeviceId;
    headers['amp-session-id'] = String(amplitudeIdentifiers.amplitudeSessionId);

    if (clientId) {
        headers[CLIENT_ID_HEADER] = clientId;
    }

    const response = await fetch(url.toString(), {
        headers,
        method,
        body: JSON.stringify(body),
    });

    if (response.status >= 400) {
        const errorResponse = (await response.json()) as PimsErrorResponse;
        throw new Error(`Failed to authorize: ${errorResponse.message}`, { cause: errorResponse });
    }

    const authorizationResult = (await response.json()) as PimsAuthorizationResponse;
    return authorizationResult;
}
